import { useEffect, useMemo, useState } from "react";

import { LoadingOverlay } from "components";
import { EmptyStatesGenetics } from "components/EmptyStates/Genetics";
import { Button } from "twComponents/Button";

import {
  useAngusEpdDataQuery,
  useBeefXDairyDataQuery,
  useBreedseekDataQuery,
  useNeogenEpdDataQuery,
} from "generated/graphql";

import { BeefXDairyDataTable } from "./BeefXDairyTable";
import { BreedseekDataTable } from "./BreedseekDataTable";
import { EpdDataTable } from "./EpdDataTable";

const Section = ({ title, toggle, show, children }) => (
  <>
    <div className="flex flex-row justify-start align-middle">
      <h3 className="font-bold">{title}</h3>
      <Button className="pb-2 text-lg" variant="ghost" size="square" onClick={toggle}>
        {show ? "-" : "+"}
      </Button>
    </div>
    <div className="pb-4">{show ? children : null}</div>
  </>
);

interface Props {
  animalId: string;
}

export const Genetics: React.FC<Props> = ({ animalId }) => {
  const [loading, setLoading] = useState(true);
  const queryHookOptions = {
    variables: {
      id: animalId,
    },
    skip: !animalId,
    fetchPolicy: "cache-and-network" as const,
    nextFetchPolicy: "cache-only" as const,
  };
  const { data: breedseekData, loading: breedseekLoading } = useBreedseekDataQuery(queryHookOptions);
  const { data: beefxdairyData, loading: beefXDairyLoading } = useBeefXDairyDataQuery(queryHookOptions);
  const { data: americanAngusEpdData, loading: americanAngusEpdLoading } = useAngusEpdDataQuery(queryHookOptions);
  const { data: neogenEpdData, loading: neogenEpdLoading } = useNeogenEpdDataQuery(queryHookOptions);

  const breedseek = breedseekData?.breedseek;
  const beefXDairy = beefxdairyData?.beefxdairy;
  const americanAngusEpd = americanAngusEpdData?.americanAngusEpd;
  const neogenEpd = neogenEpdData?.neogenEpd;

  const epdData = useMemo(() => {
    let data = {};
    if (americanAngusEpd) {
      data = americanAngusEpd;
    } else if (neogenEpd) {
      data = neogenEpd;
    }
    return data;
  }, [americanAngusEpd, neogenEpd]);

  const hasData = {
    breedseek: !!breedseek,
    beefXDairy: !!beefXDairy,
    epd: Object.keys(epdData).length > 0,
  };
  const [showTable, setShowTable] = useState({
    breedseek: hasData.breedseek,
    epd: hasData.epd,
    beefXDairy: hasData.beefXDairy,
  });

  const toggleTable = (tableName: string) => {
    setShowTable((prevState) => ({ ...prevState, [tableName]: !prevState[tableName] }));
  };

  useEffect(() => {
    if (!breedseekLoading && !beefXDairyLoading && !americanAngusEpdLoading && !neogenEpdLoading) {
      setLoading(false);
    }
  }, [breedseekLoading, beefXDairyLoading, americanAngusEpdLoading, neogenEpdLoading]);

  if (loading) {
    return <LoadingOverlay />;
  }

  if (!hasData.breedseek && !hasData.beefXDairy && !hasData.epd) {
    return <EmptyStatesGenetics />;
  }

  return (
    <>
      <Section title="Breedseek Data" toggle={() => toggleTable("breedseek")} show={showTable.breedseek}>
        <BreedseekDataTable breedseekData={breedseek} />
      </Section>
      <Section title="Beef x Dairy Data" toggle={() => toggleTable("beefXDairy")} show={showTable.beefXDairy}>
        <BeefXDairyDataTable beefXDairyData={beefXDairy} />
      </Section>
      <Section title="EPD Data" toggle={() => toggleTable("epd")} show={showTable.epd}>
        <EpdDataTable epdData={epdData} />
      </Section>
    </>
  );
};
